import React, { useEffect, useRef, useState, useCallback } from 'react';

/**
 * ReceiptImageEditor – modal for rotating and cropping a receipt image.
 *
 * Props:
 *   imageUrl – URL of the current receipt image (same-origin preview endpoint)
 *   onSave(blob) – async; receives the edited image as a JPEG Blob
 *   onCancel() – close without saving
 *
 * The image is drawn to a canvas so rotation/crop are applied at natural
 * resolution when exporting, independent of the on-screen display size.
 */
const MAX_DISPLAY = 460;
const MIN_CROP = 12; // minimum crop size in display px

export default function ReceiptImageEditor({ imageUrl, onSave, onCancel }) {
  const [img, setImg] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [rotation, setRotation] = useState(0); // 0 | 90 | 180 | 270
  const [crop, setCrop] = useState(null); // {x,y,w,h} in display coords, or null
  const [saving, setSaving] = useState(false);

  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const dragRef = useRef(null);

  // Load the image
  useEffect(() => {
    let cancelled = false;
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => { if (!cancelled) { setImg(image); setLoadError(false); } };
    image.onerror = () => { if (!cancelled) setLoadError(true); };
    image.src = imageUrl;
    return () => { cancelled = true; };
  }, [imageUrl]);

  // Natural dimensions after rotation
  const rotated = img
    ? (rotation % 180 === 0
        ? { w: img.naturalWidth, h: img.naturalHeight }
        : { w: img.naturalHeight, h: img.naturalWidth })
    : { w: 0, h: 0 };

  const scale = rotated.w > 0 ? Math.min(1, MAX_DISPLAY / rotated.w, MAX_DISPLAY / rotated.h) : 1;
  const dispW = Math.max(1, Math.round(rotated.w * scale));
  const dispH = Math.max(1, Math.round(rotated.h * scale));

  // Draw the rotated image to the display canvas
  useEffect(() => {
    if (!img) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = dispW;
    canvas.height = dispH;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, dispW, dispH);
    ctx.save();
    ctx.translate(dispW / 2, dispH / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    const drawW = rotation % 180 === 0 ? dispW : dispH;
    const drawH = rotation % 180 === 0 ? dispH : dispW;
    ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);
    ctx.restore();
  }, [img, rotation, dispW, dispH]);

  // Reset the crop whenever rotation changes (coordinates no longer valid)
  useEffect(() => { setCrop(null); }, [rotation]);

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  const pointerPos = useCallback((e) => {
    const rect = containerRef.current.getBoundingClientRect();
    return {
      x: clamp(e.clientX - rect.left, 0, dispW),
      y: clamp(e.clientY - rect.top, 0, dispH),
    };
  }, [dispW, dispH]);

  const onPointerMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    const { x, y } = pointerPos(e);
    if (d.mode === 'new') {
      const nx = Math.min(d.startX, x);
      const ny = Math.min(d.startY, y);
      setCrop({ x: nx, y: ny, w: Math.abs(x - d.startX), h: Math.abs(y - d.startY) });
    } else if (d.mode === 'move') {
      const nx = clamp(d.orig.x + (x - d.startX), 0, dispW - d.orig.w);
      const ny = clamp(d.orig.y + (y - d.startY), 0, dispH - d.orig.h);
      setCrop({ x: nx, y: ny, w: d.orig.w, h: d.orig.h });
    } else if (d.mode === 'resize') {
      // Opposite corner stays fixed
      const nx = Math.min(d.fixedX, x);
      const ny = Math.min(d.fixedY, y);
      setCrop({ x: nx, y: ny, w: Math.abs(x - d.fixedX), h: Math.abs(y - d.fixedY) });
    }
  }, [pointerPos, dispW, dispH]);

  const endDrag = useCallback(() => {
    const d = dragRef.current;
    dragRef.current = null;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', endDrag);
    // Discard tiny selections
    setCrop((c) => (c && (c.w < MIN_CROP || c.h < MIN_CROP) ? null : c));
    if (d) { /* no-op */ }
  }, [onPointerMove]);

  const startDrag = useCallback((mode, e, extra = {}) => {
    e.preventDefault();
    e.stopPropagation();
    const { x, y } = pointerPos(e);
    dragRef.current = { mode, startX: x, startY: y, orig: crop, ...extra };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', endDrag);
  }, [crop, pointerPos, onPointerMove, endDrag]);

  const rotate = (deg) => setRotation((r) => (((r + deg) % 360) + 360) % 360);

  const handleSave = async () => {
    if (!img || saving) return;
    setSaving(true);
    try {
      // Full rotated image at natural resolution
      const full = document.createElement('canvas');
      full.width = rotated.w;
      full.height = rotated.h;
      const fctx = full.getContext('2d');
      fctx.translate(rotated.w / 2, rotated.h / 2);
      fctx.rotate((rotation * Math.PI) / 180);
      const dw = rotation % 180 === 0 ? rotated.w : rotated.h;
      const dh = rotation % 180 === 0 ? rotated.h : rotated.w;
      fctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);

      // Crop region in natural coordinates
      let sx = 0, sy = 0, sw = rotated.w, sh = rotated.h;
      if (crop && crop.w >= MIN_CROP && crop.h >= MIN_CROP && scale > 0) {
        sx = Math.round(crop.x / scale);
        sy = Math.round(crop.y / scale);
        sw = Math.round(crop.w / scale);
        sh = Math.round(crop.h / scale);
        sw = Math.min(sw, rotated.w - sx);
        sh = Math.min(sh, rotated.h - sy);
      }

      const out = document.createElement('canvas');
      out.width = Math.max(1, sw);
      out.height = Math.max(1, sh);
      out.getContext('2d').drawImage(full, sx, sy, sw, sh, 0, 0, out.width, out.height);

      const blob = await new Promise((resolve) => out.toBlob(resolve, 'image/jpeg', 0.9));
      if (!blob) throw new Error('Could not export image');
      await onSave(blob);
    } catch (e) {
      alert(e.message || 'Failed to save image');
      setSaving(false);
    }
    // On success the parent closes the editor; no need to unset saving.
  };

  const handleStyle = {
    position: 'absolute', width: '14px', height: '14px', background: 'white',
    border: '2px solid var(--primary, #2563eb)', borderRadius: '2px', boxSizing: 'border-box',
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
      onClick={saving ? undefined : onCancel}
    >
      <div
        style={{ background: 'white', borderRadius: '10px', padding: '16px', maxWidth: '95vw', maxHeight: '95vh', overflow: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontSize: '15px', fontWeight: 600, alignSelf: 'stretch' }}>Edit Receipt Image</div>

        {loadError ? (
          <div style={{ padding: '30px', color: 'var(--danger, #dc2626)' }}>Failed to load image.</div>
        ) : !img ? (
          <div style={{ padding: '30px', color: 'var(--gray-500)' }}>Loading image…</div>
        ) : (
          <>
            <div
              ref={containerRef}
              style={{ position: 'relative', width: `${dispW}px`, height: `${dispH}px`, touchAction: 'none', userSelect: 'none', cursor: 'crosshair' }}
              onPointerDown={(e) => startDrag('new', e)}
            >
              <canvas ref={canvasRef} style={{ display: 'block', width: `${dispW}px`, height: `${dispH}px` }} />
              {crop && (
                <div
                  style={{
                    position: 'absolute', left: `${crop.x}px`, top: `${crop.y}px`, width: `${crop.w}px`, height: `${crop.h}px`,
                    border: '1px solid var(--primary, #2563eb)', boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)', cursor: 'move', boxSizing: 'border-box',
                  }}
                  onPointerDown={(e) => startDrag('move', e)}
                >
                  {[
                    { key: 'nw', style: { left: '-7px', top: '-7px', cursor: 'nwse-resize' }, fixedX: crop.x + crop.w, fixedY: crop.y + crop.h },
                    { key: 'ne', style: { right: '-7px', top: '-7px', cursor: 'nesw-resize' }, fixedX: crop.x, fixedY: crop.y + crop.h },
                    { key: 'sw', style: { left: '-7px', bottom: '-7px', cursor: 'nesw-resize' }, fixedX: crop.x + crop.w, fixedY: crop.y },
                    { key: 'se', style: { right: '-7px', bottom: '-7px', cursor: 'nwse-resize' }, fixedX: crop.x, fixedY: crop.y },
                  ].map((h) => (
                    <div
                      key={h.key}
                      style={{ ...handleStyle, ...h.style }}
                      onPointerDown={(e) => startDrag('resize', e, { fixedX: h.fixedX, fixedY: h.fixedY })}
                    />
                  ))}
                </div>
              )}
            </div>

            <div style={{ fontSize: '12px', color: 'var(--gray-500)', textAlign: 'center' }}>
              Drag on the image to select a crop area. Use the handles to adjust, or drag inside to move.
            </div>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
              <button className="btn btn-sm btn-outline" onClick={() => rotate(-90)} disabled={saving}>↺ Rotate Left</button>
              <button className="btn btn-sm btn-outline" onClick={() => rotate(90)} disabled={saving}>↻ Rotate Right</button>
              <button className="btn btn-sm btn-outline" onClick={() => setCrop(null)} disabled={saving || !crop}>Clear Crop</button>
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: '8px', alignSelf: 'stretch', marginTop: '4px' }}>
          <button className="btn btn-outline" style={{ flex: 1 }} onClick={onCancel} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleSave} disabled={saving || !img || loadError}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
